/**
 * Super Admin — Users
 *
 * Lists every user and controls which workspaces each one can reach.
 *
 * This page is not a security boundary. It renders for anyone who navigates to
 * /admin/users; a non-master simply gets errors from every RPC and an empty
 * table. That is intended — is_platform_master() inside each function is the
 * real gate, and this file must never grow a second one that could disagree
 * with it.
 *
 * @module pages/admin/Users
 */

import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import { YDMPageHero } from "../../components/brand";
import { ManageAccessModal } from "../../components/admin/ManageAccessModal";
import { AddUserModal } from "../../components/admin/AddUserModal";
import { DeleteUserModal } from "../../components/admin/DeleteUserModal";
import { getSupabaseClient } from "../../config/supabase";
import {
  listUsers,
  listWorkspaces,
  type AdminUser,
  type AdminWorkspace,
} from "../../services/adminService";

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [workspaces, setWorkspaces] = useState<AdminWorkspace[]>([]);
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState<AdminUser | null>(null);
  const [masterId, setMasterId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // `silent` distinguishes the initial page load (full-page spinner is fine —
  // there is nothing on screen yet) from a post-save refresh (the master is
  // looking at the table and the open Manage access modal; unmounting both
  // behind a spinner on every save would be a jarring flash for what should
  // be a small in-place update).
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const [usersResult, workspacesResult] = await Promise.all([
      listUsers(),
      listWorkspaces(),
    ]);

    // `?? ` on both branches, and it is not defensive noise: ServiceResult is
    // `{ success: boolean; data?: T; error?: string }` — not a discriminated
    // union — so `success` does not narrow `data` or `error`, and `strict` makes
    // the optionality a compile error otherwise. See Task 4 Step 1.
    //
    // Apply whichever result succeeded rather than bailing out on the first
    // failure: a `listUsers` success must not be discarded just because the
    // paired `listWorkspaces` call failed (or vice versa), or the table keeps
    // asserting access that was already revoked server-side.
    if (usersResult.success) setUsers(usersResult.data ?? []);
    if (workspacesResult.success) setWorkspaces(workspacesResult.data ?? []);

    const failure = !usersResult.success
      ? usersResult.error ?? "Failed to load users"
      : !workspacesResult.success
        ? workspacesResult.error ?? "Failed to load workspaces"
        : null;
    setError(failure);

    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The only user who can open this page is the master, so the caller's own id
  // is the master's id. This drives the disabled state; the real refusal is in
  // the route.
  useEffect(() => {
    let ignore = false;
    void getSupabaseClient()
      .auth.getSession()
      .then(({ data }) => {
        if (!ignore) setMasterId(data.session?.user.id ?? null);
      });
    return () => {
      ignore = true;
    };
  }, []);

  // Hands the open modal a fresh `user` object after load() returns new rows.
  // This exists for exactly one reason — do not drop it as a redundant sync:
  //
  // ManageAccessModal derives its `original` baseline from the `user` prop by
  // reference, and after a partial-failure Save it stays open with the draft
  // intact. A row that did succeed only stops counting as "pending" once that
  // baseline catches up, which is this effect handing over a new object. Skip
  // it and a retry would resend writes that already landed.
  //
  // Note it does NOT affect what the modal displays: every row renders from the
  // modal's own draft, and the only thing read off this prop for display is the
  // email in the title. See the contract note above `original` in
  // ManageAccessModal.tsx.
  useEffect(() => {
    if (!selected) return;
    const fresh = users.find((u) => u.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [users, selected]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-ink-900 animate-spin" />
      </div>
    );
  }

  if (error && users.length === 0) {
    return (
      <div className="space-y-4 animate-fade-in">
        <YDMPageHero
          eyebrow="Super Admin"
          headline="USERS"
          subtitle="Control which workspaces each user can reach."
        />
        <div className="bg-white rounded-lg border border-neutral-200 p-8 text-center">
          <ShieldAlert className="w-12 h-12 text-yellow-700 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-ink-900 mb-2">
            This area is for the platform master
          </h2>
          <p className="text-neutral-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <YDMPageHero
        eyebrow="Super Admin"
        headline="USERS"
        subtitle="Control which workspaces each user can reach."
      />

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="px-3 py-1.5 text-xs font-medium rounded-md border border-neutral-300 hover:bg-neutral-50"
        >
          Add user
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/*
        One set of markup, two layouts, so each user is in the DOM once at
        every width:
        - xl and up: a real table.
        - Below xl: each row is a small grid. Email and last sign-in sit on
          the left, the buttons on the right, the chips underneath; phones
          get a single column. Not lg: the sidebar pins at 1024px, which
          leaves this box 687px for a table that needed 866px with today's
          data, and the buttons ended up behind a sideways scroll.
          `grid-cols-1` is not redundant: an implicit column is `auto` and
          grows to the longest unbreakable email, so nothing wraps or truncates.

        `relative` keeps the sr-only "Actions" label (position:absolute)
        inside this box's overflow. The old header cell was sr-only itself
        and, with no positioned ancestor, stretched the whole page sideways on
        phones (a 390px viewport scrolled to 499px).
      */}
      <div className="relative bg-white rounded-lg border border-neutral-200 xl:overflow-x-auto">
        <table className="block xl:table w-full text-sm">
          <thead className="hidden xl:table-header-group bg-neutral-50 text-left text-neutral-700">
            <tr>
              <th className="px-4 py-3 font-medium w-1/4">Email</th>
              <th className="px-4 py-3 font-medium">Workspaces</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Last sign-in</th>
              <th className="px-4 py-3 w-px">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="block xl:table-row-group divide-y divide-neutral-100">
            {users.map((user) => (
              <tr
                key={user.id}
                className="grid grid-cols-1 gap-x-4 gap-y-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] xl:table-row xl:p-0"
              >
                {/* overflow-wrap:anywhere lets a long address wrap instead of
                    widening the table and pushing the buttons off-screen. The
                    header's w-1/4 is the other half: without a set width the
                    table squeezes this column to almost nothing (87px at
                    1440px) and gives the space to the chips. */}
                <td className="row-start-1 xl:table-cell xl:px-4 xl:py-3 font-medium xl:font-normal text-ink-900 [overflow-wrap:anywhere]">
                  {user.email}
                </td>
                <td className="row-start-3 sm:col-span-2 xl:table-cell xl:px-4 xl:py-3">
                  {user.workspaces.length === 0 ? (
                    <span className="text-neutral-700">No access</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {user.workspaces.map((w) => (
                        // One line per chip: the name truncates (full text in
                        // the tooltip) instead of breaking inside the pill. The
                        // fixed xl cap matters: in a table, max-w-full is
                        // measured against the column itself, so it cannot
                        // stop one long name from widening the column.
                        <span
                          key={w.id}
                          title={`${w.name} · ${w.role}`}
                          className="inline-flex max-w-full xl:max-w-[20rem] items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 text-xs whitespace-nowrap"
                        >
                          <span className="min-w-0 truncate">{w.name}</span>
                          <span aria-hidden="true">·</span>
                          <span className="shrink-0">{w.role}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="row-start-2 xl:table-cell xl:px-4 xl:py-3 text-xs xl:text-sm text-neutral-700 whitespace-nowrap">
                  <span className="xl:hidden">Last sign-in: </span>
                  {user.lastSignInAt
                    ? new Date(user.lastSignInAt).toLocaleDateString()
                    : "Never"}
                </td>
                {/* w-px + nowrap: the column is exactly as wide as its two
                    buttons, so they never wrap or stack. */}
                <td className="row-start-4 sm:col-start-2 sm:row-start-1 sm:row-span-2 sm:self-center xl:table-cell xl:px-4 xl:py-3 xl:w-px whitespace-nowrap">
                  <div className="flex items-center gap-2 xl:justify-end">
                    <button
                      type="button"
                      onClick={() => setSelected(user)}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-neutral-300 hover:bg-neutral-50"
                    >
                      Manage access
                    </button>
                    <button
                      type="button"
                      onClick={() => setToDelete(user)}
                      // Disabled until masterId resolves, not only for the master's own
                      // row: until we know who the master is, we cannot know that this
                      // row is not them, and delete is irreversible. The route refuses a
                      // master delete regardless — this just closes the brief window
                      // where the button would be live before getSession returns.
                      disabled={masterId === null || user.id === masterId}
                      title={user.id === masterId ? "The platform master cannot be deleted" : undefined}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ManageAccessModal
        isOpen={selected !== null}
        user={selected}
        workspaces={workspaces}
        onClose={() => setSelected(null)}
        onSaved={() => load(true)}
      />

      <AddUserModal
        isOpen={addOpen}
        workspaces={workspaces}
        onClose={() => setAddOpen(false)}
        onCreated={() => load(true)}
      />

      <DeleteUserModal
        isOpen={toDelete !== null}
        user={toDelete}
        isMaster={toDelete?.id === masterId}
        onClose={() => setToDelete(null)}
        onDeleted={() => load(true)}
      />
    </div>
  );
};

export default AdminUsers;
